// Checklist: studioaeye-backend/docs/tests-Backend/testingCheckList.md
// Features: ML001-F0034, ML002-F0022, M001-D022-F013, M004-F0672, M004-F0673, M004-F0701
// Tests: T-M004-0130, T-M004-0133, T-M004-0179, T-M004-0181, T-LED-NODEUI-004, T-LED-WEB-001, T-LED-WEB-003, T-BK-UI-003, T-BK-ORG-AUTH-001

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import Layout from './layout/Layout';
import StepUpDialog from './auth/StepUpDialog';
import EnvelopesWorkspace from './envelopes/EnvelopesWorkspace';
import InvoicesTab from './invoices/InvoicesTab';
import { canViewInvoices } from './invoices/invoiceModel';
import CreditNotesTab from './ledger/CreditNotesTab';
import { canManageCreditNotes, canOpenCreditNotes } from './ledger/creditNoteModel';
import ClaimsReviewTab from './claims/ClaimsReviewTab';
import { canReviewClaims, submittedCount, queueOf } from './claims/claimReviewModel';
import { claimReviewApi } from '../services/claimReviewApi';
import { ledgerNodeApi, organisationApi, userApi } from '../services/api';
import { getStepUpChallengeFromError } from '../utils/stepUp';
import {
  INSTALLATION_NOT_FOUND, INSTALLATION_REQUIRED, OFFLINE_REASON, coreErrorCode, coreErrorMessage,
  installationName, lastSeenText, normaliseInstallations,
} from './ledgerInstallationPicker';
import './LedgerLandingPage.css';

const payload = (response) => response?.data?.data || response?.data || response || {};
const organisationId = (organisation) => Number(organisation?.id || organisation?.organisation_id || 0);
const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const organisationName = (organisation) => organisation?.name || organisation?.organisation_name || `Organisation ${organisationId(organisation)}`;

export function selectLedgerLandingOrganisations(organisationResponse, accessResponse) {
  const organisationPayload = payload(organisationResponse);
  const organisations = Array.isArray(organisationPayload)
    ? organisationPayload
    : Array.isArray(organisationPayload.organisations)
      ? organisationPayload.organisations
      : Array.isArray(organisationPayload.items) ? organisationPayload.items : [];
  const applications = payload(accessResponse)?.applications || {};
  const ledgerIds = new Set((applications.ledger?.organisationIds || []).map(Number));
  const bookkeepingIds = new Set((applications.bookkeeping?.organisationIds || []).map(Number));
  const ledgerManageIds = new Set((applications.ledgerManage?.organisationIds || []).map(Number));

  return organisations
    .filter((organisation) => ledgerIds.has(organisationId(organisation)) || bookkeepingIds.has(organisationId(organisation)))
    .map((organisation) => ({
      ...organisation,
      canLedger: ledgerIds.has(organisationId(organisation)),
      canBookkeeping: bookkeepingIds.has(organisationId(organisation)),
      canManageMembers: ledgerManageIds.has(organisationId(organisation)),
    }));
}

// M004-F0701 — the inline installation picker (no modal). Rows come from Core's
// SAL-3 list; an offline installation is listed and marked, and cannot be opened
// (Core's hand-off answers 404 LEDGER_NODE_OFFLINE for it).
function InstallationPicker({ picker, organisationName: name, busy, onOpen, onRefresh }) {
  return (
    <section className="ledger-installations" data-testid="ledger-installation-picker" aria-label={`Ledger servers for ${name}`}>
      <h3>Choose a Ledger server</h3>
      {picker.notice ? <p className="ledger-landing-error" role="alert">{picker.notice}</p> : null}
      {picker.status === 'loading' ? <p role="status">Loading this organisation’s Ledger servers…</p> : null}
      {picker.status === 'error' ? <p className="ledger-landing-error" role="alert">{picker.message}</p> : null}
      {picker.status === 'empty' ? (
        <p role="status" data-testid="ledger-installations-empty">
          No Ledger server is connected to this organisation.{picker.message ? ` ${picker.message}` : ''}
        </p>
      ) : null}
      {picker.status === 'ready' ? (
        <ul className="ledger-installation-list">
          {picker.installations.map((installation) => (
            <li className="ledger-installation-row" key={installation.installationUuid} data-testid="ledger-installation-row" data-installation-uuid={installation.installationUuid}>
              <span className="ledger-installation-name">{installationName(installation)}</span>
              <span className={`ledger-installation-status ${installation.online ? 'online' : 'offline'}`}>{installation.online ? 'Online' : 'Offline'}</span>
              <small>{lastSeenText(installation)}</small>
              <button
                type="button"
                className="ledger-installation-open"
                disabled={busy || !installation.online}
                aria-label={`Open ${installationName(installation)}`}
                onClick={() => onOpen(installation.installationUuid)}
              >
                Open
              </button>
              {installation.online ? null : <small className="ledger-installation-reason">{OFFLINE_REASON}</small>}
            </li>
          ))}
        </ul>
      ) : null}
      {picker.status !== 'loading' ? <button type="button" className="ledger-installation-refresh" onClick={onRefresh} disabled={busy}>Refresh</button> : null}
    </section>
  );
}

export default function LedgerLandingPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedTab = searchParams.get('tab');
  const openEnvelopeId = searchParams.get('envelope');
  // M004-F0749 (WebUI #191): the Invoices tab. Only organisations where the viewer holds billing_transactions_view or ledger_manage
  // are offered (fail closed: an unreadable projection offers none). null = still checking.
  const [invoiceOrgs, setInvoiceOrgs] = useState(null);
  // M004-F0751 (WebUI #193): the Credit notes tab. The same gate as Invoices (billing_transactions_view or ledger_manage); fail closed. null = checking.
  const [creditOrgs, setCreditOrgs] = useState(null);
  // M004-F0748 (WebUI #188): the Claims to review tab. Only organisations where the viewer holds ledger_manage (Core my-permissions data, never a role
  // name); fail closed. null = still checking. claimCounts = submitted claims per organisation (the tab's count badge).
  const [claimOrgs, setClaimOrgs] = useState(null);
  const [claimCounts, setClaimCounts] = useState({});
  const setClaimCount = useCallback((id, n) => setClaimCounts((current) => (current[id] === n ? current : { ...current, [id]: n })), []);
  const activeTab = requestedTab === 'invoices' && (invoiceOrgs === null || invoiceOrgs.length)
    ? 'invoices'
    : requestedTab === 'credit-notes' && (creditOrgs === null || creditOrgs.length)
      ? 'credit-notes'
      : requestedTab === 'claims' && (claimOrgs === null || claimOrgs.length)
        ? 'claims'
        : (requestedTab === 'envelopes' || openEnvelopeId ? 'envelopes' : 'organisations');
  // M004-F0672: the old /Ledger page's one extra capability. A Ledger node links
  // here with the exact installation it wants; Core still verifies it against
  // the organisation (LEDGER_INSTALLATION_SCOPE_DENIED), the UI only forwards.
  const requestedInstallation = searchParams.get('installationUuid');
  const targetInstallationUuid = UUID_PATTERN.test(requestedInstallation || '') ? requestedInstallation : undefined;
  const [organisations, setOrganisations] = useState([]);
  // M004-F0747: returned claims waiting for the claimant, per organisation (reported by the Envelopes workspace); shown on the tab.
  const [attention, setAttention] = useState({});
  const reportAttention = useCallback((orgKey, count) => {
    setAttention((current) => (current[orgKey] === count ? current : { ...current, [orgKey]: count }));
  }, []);
  const attentionTotal = Object.values(attention).reduce((sum, n) => sum + n, 0);
  const [loading, setLoading] = useState(true);
  const [openingOrganisationId, setOpeningOrganisationId] = useState(null);
  const [stepUpChallenge, setStepUpChallenge] = useState(null);
  const [error, setError] = useState('');
  // M004-F0701: the inline installation picker of ONE organisation card at a
  // time. { orgId, status: loading|ready|empty|error, installations, message, notice }
  const [picker, setPicker] = useState(null);
  const pickerRequest = useRef(0);
  // What to retry after a SAL step-up: the organisation AND the chosen installation.
  const pendingOpen = useRef(null);

  useEffect(() => {
    let current = true;
    Promise.all([organisationApi.getMine(), userApi.getApplicationAccess()])
      .then(([organisationResponse, accessResponse]) => {
        if (current) setOrganisations(selectLedgerLandingOrganisations(organisationResponse, accessResponse));
      })
      .catch((cause) => {
        if (current) setError(cause?.response?.data?.message || cause?.message || 'StudioAEye could not load your Ledger organisations.');
      })
      .finally(() => {
        if (current) setLoading(false);
      });
    return () => { current = false; };
  }, []);

  useEffect(() => {
    if (loading) return undefined;
    let current = true;
    Promise.all(organisations.filter((organisation) => organisation.canLedger).map(async (organisation) => {
      try {
        const response = await organisationApi.getMyPermissions(organisationId(organisation));
        const permissions = payload(response)?.permissions;
        const entry = { id: organisationId(organisation), name: organisationName(organisation), manage: canManageCreditNotes(permissions) };
        return {
          invoice: canViewInvoices(permissions) ? entry : null,
          credit: canOpenCreditNotes(permissions) ? entry : null,
          claim: canReviewClaims(permissions) ? entry : null,
        };
      } catch { return { invoice: null, credit: null, claim: null }; }
    })).then((found) => {
      if (!current) return;
      setInvoiceOrgs(found.map((item) => item.invoice).filter(Boolean));
      setCreditOrgs(found.map((item) => item.credit).filter(Boolean));
      setClaimOrgs(found.map((item) => item.claim).filter(Boolean));
    });
    return () => { current = false; };
  }, [loading, organisations]);

  const loadPicker = useCallback(async (orgId, notice = '') => {
    const request = pickerRequest.current + 1;
    pickerRequest.current = request;
    setPicker({ orgId, status: 'loading', installations: [], message: '', notice });
    try {
      const installations = normaliseInstallations(await ledgerNodeApi.listInstallations(orgId));
      if (pickerRequest.current !== request) return;
      setPicker({ orgId, status: installations.length ? 'ready' : 'empty', installations, message: '', notice });
    } catch (cause) {
      if (pickerRequest.current !== request) return;
      setPicker({ orgId, status: 'error', installations: [], message: coreErrorMessage(cause, 'StudioAEye could not list this organisation\u2019s Ledger servers.'), notice });
    }
  }, []);

  // `installationUuid` is the row the user chose in the picker (M004-F0701); the
  // deep-link uuid (M004-F0672) is the fallback. Core verifies either.
  const openLedger = useCallback(async (orgId, chosenInstallationUuid) => {
    const installationUuid = chosenInstallationUuid || targetInstallationUuid;
    pendingOpen.current = { orgId, installationUuid: chosenInstallationUuid };
    setOpeningOrganisationId(orgId);
    setError('');
    try {
      const response = installationUuid
        ? await ledgerNodeApi.createHandoff(orgId, installationUuid)
        : await ledgerNodeApi.createHandoff(orgId);
      const workspaceUrl = payload(response).workspaceUrl;
      if (!workspaceUrl) throw new Error('StudioAEye did not return a Ledger workspace address.');
      window.location.assign(workspaceUrl);
    } catch (cause) {
      const challenge = getStepUpChallengeFromError(cause);
      if (challenge?.id) {
        setStepUpChallenge(challenge);
        return;
      }
      setOpeningOrganisationId(null);
      const code = coreErrorCode(cause);
      const message = coreErrorMessage(cause, 'StudioAEye could not open this organisation Ledger.');
      if (!installationUuid && code === INSTALLATION_REQUIRED) {
        // Core's refusal stays verbatim (T-M004-0133); the picker is the way forward.
        setError(message);
        loadPicker(orgId);
      } else if (!installationUuid && code === INSTALLATION_NOT_FOUND) {
        pickerRequest.current += 1;
        setPicker({ orgId, status: 'empty', installations: [], message, notice: '' });
      } else if (chosenInstallationUuid) {
        // A row that went offline between the list and the click: Core's words, list re-read.
        loadPicker(orgId, message);
      } else {
        setError(message);
      }
    }
  }, [targetInstallationUuid, loadPicker]);

  const selectTab = useCallback((tab) => {
    setSearchParams(tab === 'envelopes' || tab === 'invoices' || tab === 'credit-notes' || tab === 'claims' ? { tab } : {});
  }, [setSearchParams]);
  // Arrow keys move between the tabs (WAI-ARIA tabs); each arrow selects the tab it lands on.
  const onTabKeyDown = useCallback((event) => {
    if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
    const tabs = [...event.currentTarget.querySelectorAll('[role="tab"]')];
    const at = tabs.indexOf(document.activeElement);
    if (at < 0) return;
    event.preventDefault();
    const next = event.key === 'Home' ? 0 : event.key === 'End' ? tabs.length - 1 : (at + (event.key === 'ArrowRight' ? 1 : tabs.length - 1)) % tabs.length;
    tabs[next].focus();
    tabs[next].click();
  }, []);
  const invoiceOrgId = invoiceOrgs?.some((item) => String(item.id) === searchParams.get('org')) ? searchParams.get('org') : String(invoiceOrgs?.[0]?.id ?? '');
  const creditOrgId = creditOrgs?.some((item) => String(item.id) === searchParams.get('org')) ? searchParams.get('org') : String(creditOrgs?.[0]?.id ?? '');
  const claimOrgId = claimOrgs?.some((item) => String(item.id) === searchParams.get('org')) ? searchParams.get('org') : String(claimOrgs?.[0]?.id ?? '');
  const selectClaimOrg = useCallback((id) => setSearchParams({ tab: 'claims', org: id }), [setSearchParams]);
  const claimBadge = Object.values(claimCounts).reduce((sum, n) => sum + n, 0);
  // The badge is the count of submitted claims; it is read once per reviewable organisation, then kept current by the open tab.
  useEffect(() => {
    if (!claimOrgs || !claimOrgs.length) return undefined;
    let current = true;
    claimOrgs.forEach((item) => {
      claimReviewApi.queue(item.id, 'submitted').then((response) => { if (current) setClaimCount(item.id, submittedCount(queueOf(response))); }).catch(() => {});
    });
    return () => { current = false; };
  }, [claimOrgs, setClaimCount]);
  const selectInvoiceOrg = useCallback((id) => setSearchParams({ tab: 'invoices', org: id }), [setSearchParams]);
  const selectCreditOrg = useCallback((id) => setSearchParams({ tab: 'credit-notes', org: id }), [setSearchParams]);
  const openEnvelope = useCallback((envelopeIdToOpen) => {
    setSearchParams({ tab: 'envelopes', envelope: envelopeIdToOpen });
  }, [setSearchParams]);
  const dismissEnvelope = useCallback(() => {
    setSearchParams({ tab: 'envelopes' });
  }, [setSearchParams]);

  return (
    <Layout>
      <div className="ledger-landing">
        <StepUpDialog
          challenge={stepUpChallenge}
          title="Verify Ledger access"
          description="Confirm your identity before StudioAEye opens this organisation Ledger."
          onComplete={() => {
            const retry = pendingOpen.current;
            setStepUpChallenge(null);
            if (retry?.orgId) openLedger(retry.orgId, retry.installationUuid);
          }}
          onCancel={() => {
            setStepUpChallenge(null);
            setOpeningOrganisationId(null);
            setError('Verification was cancelled.');
          }}
        />

        {/* M004-F0636 (UI-3): Envelopes is a tab on the existing Ledger page,
            addressed as /app/ledger?tab=envelopes (the Bookings Hub ?tab=
            convention). No new page, no new route, nothing in the nav changes. */}
        <div className="ledger-tabs" role="tablist" aria-label="Ledger sections" onKeyDown={onTabKeyDown}>
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === 'organisations'}
            className={`ledger-tab${activeTab === 'organisations' ? ' active' : ''}`}
            data-testid="ledger-tab-organisations"
            onClick={() => selectTab('organisations')}
          >
            Organisations
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === 'envelopes'}
            className={`ledger-tab${activeTab === 'envelopes' ? ' active' : ''}`}
            data-testid="ledger-tab-envelopes"
            onClick={() => selectTab('envelopes')}
          >
            My Envelopes
            {attentionTotal > 0 ? (
              <>
                {' '}
                <span className="ledger-tab-count" data-testid="ledger-tab-envelopes-count" aria-hidden="true">{attentionTotal}</span>
                <span className="ledger-visually-hidden" data-testid="ledger-tab-envelopes-count-text">{` (${attentionTotal} returned ${attentionTotal === 1 ? 'claim needs' : 'claims need'} your attention)`}</span>
              </>
            ) : null}
          </button>
          {creditOrgs && creditOrgs.length ? (
            <button
              type="button"
              role="tab"
              aria-selected={activeTab === 'credit-notes'}
              className={`ledger-tab${activeTab === 'credit-notes' ? ' active' : ''}`}
              data-testid="ledger-tab-credit-notes"
              onClick={() => selectTab('credit-notes')}
            >
              Credit notes
            </button>
          ) : null}
          {invoiceOrgs && invoiceOrgs.length ? (
            <button
              type="button"
              role="tab"
              aria-selected={activeTab === 'invoices'}
              className={`ledger-tab${activeTab === 'invoices' ? ' active' : ''}`}
              data-testid="ledger-tab-invoices"
              onClick={() => selectTab('invoices')}
            >
              Invoices
            </button>
          ) : null}
          {claimOrgs && claimOrgs.length ? (
            <button
              type="button"
              role="tab"
              aria-selected={activeTab === 'claims'}
              className={`ledger-tab${activeTab === 'claims' ? ' active' : ''}`}
              data-testid="ledger-tab-claims"
              onClick={() => selectTab('claims')}
            >
              Claims to review{claimBadge > 0 ? <span className="ledger-tab-badge" data-testid="ledger-tab-claims-count"> ({claimBadge})</span> : null}
            </button>
          ) : null}
        </div>

        {activeTab === 'credit-notes' && creditOrgs === null ? (
          <p className="ledger-landing-state" role="status" data-testid="credit-notes-checking">Checking your credit-note access…</p>
        ) : activeTab === 'credit-notes' ? (
          <CreditNotesTab organisations={creditOrgs} orgId={creditOrgId} onSelectOrg={selectCreditOrg} />
        ) : activeTab === 'claims' && claimOrgs === null ? (
          <p className="ledger-landing-state" role="status" data-testid="claims-checking">Checking your review access…</p>
        ) : activeTab === 'claims' ? (
          <ClaimsReviewTab organisations={claimOrgs} orgId={claimOrgId} onSelectOrg={selectClaimOrg} onSubmittedCount={setClaimCount} />
        ) : activeTab === 'invoices' && invoiceOrgs === null ? (
          <p className="ledger-landing-state" role="status" data-testid="invoices-checking">Checking your invoice access…</p>
        ) : activeTab === 'invoices' ? (
          <InvoicesTab
            organisations={invoiceOrgs}
            orgId={invoiceOrgId}
            onSelectOrg={selectInvoiceOrg}
          />
        ) : activeTab === 'envelopes' ? (
          <EnvelopesWorkspace
            organisations={organisations}
            organisationsLoading={loading}
            envelopeId={activeTab === 'envelopes' ? openEnvelopeId : null}
            onOpenEnvelope={openEnvelope}
            onDismissEnvelope={dismissEnvelope}
            onAttentionCount={reportAttention}
          />
        ) : (
          <>
            <header className="ledger-landing-heading">
              <span>Ledger</span>
              <h1>Choose an organisation</h1>
              <p>Each organisation has its own permissions, bookkeeping workspace and connected Ledger.</p>
              {targetInstallationUuid ? (
                <p className="ledger-landing-installation" data-testid="ledger-landing-installation" role="status">
                  Opening a specific Ledger server ({targetInstallationUuid}). Choose the organisation it belongs to.
                </p>
              ) : null}
            </header>

            {loading ? <p className="ledger-landing-state" role="status">Checking your organisation access…</p> : null}
            {error ? <p className="ledger-landing-error" role="alert">{error}</p> : null}
            {!loading && !error && !organisations.length ? (
              <section className="ledger-landing-state">
                <h2>No Ledger organisations</h2>
                <p>Your StudioAEye login does not currently have Ledger access for an organisation.</p>
              </section>
            ) : null}
          </>
        )}

        <section className="ledger-organisation-grid" aria-label="Your Ledger organisations">
          {activeTab === 'organisations' && organisations.map((organisation) => {
            const id = organisationId(organisation);
            const name = organisationName(organisation);
            const opening = openingOrganisationId === id;
            return (
              <article className="ledger-organisation-card" key={id}>
                <div className="ledger-organisation-title">
                  <span>Organisation</span>
                  <h2>{name}</h2>
                </div>
                <div className="ledger-tool-list">
                  {organisation.canBookkeeping ? (
                    <Link className="ledger-tool" to={`/app/ledger/${id}/bookkeeping`}>
                      <span aria-hidden="true">🧾</span>
                      <span><strong>Booking Keeper</strong><small>Receipts, expenses and bookkeeping</small></span>
                      <span aria-hidden="true">→</span>
                    </Link>
                  ) : null}
                  {organisation.canManageMembers ? (
                    <Link className="ledger-tool" to={`/app/ledger/${id}/members`}>
                      <span aria-hidden="true">👥</span>
                      <span><strong>Member accounts</strong><small>Open a person’s Ledger and add credit</small></span>
                      <span aria-hidden="true">→</span>
                    </Link>
                  ) : null}
                  {organisation.canManageMembers ? (
                    <Link className="ledger-tool" to={`/app/ledger/${id}/billing-settings`} data-testid="ledger-tool-billing-settings">
                      <span aria-hidden="true">🏦</span>
                      <span><strong>Billing settings</strong><small>Issuer, bank details, payment terms and invoice numbering</small></span>
                      <span aria-hidden="true">→</span>
                    </Link>
                  ) : null}
                  {organisation.canLedger ? (
                    <Link className="ledger-tool ledger-tool-secondary" to={`/app/ledger/${id}/statement`} data-testid="ledger-tool-statement">
                      <span aria-hidden="true">🧾</span>
                      <span><strong>My statement</strong><small>Your draft invoice and this month's advice</small></span>
                      <span aria-hidden="true">→</span>
                    </Link>
                  ) : null}
                  {organisation.canLedger ? (
                    <Link className="ledger-tool ledger-tool-secondary" to={`/app/ledger/${id}/account`}>
                      <span aria-hidden="true">👤</span>
                      <span><strong>My account</strong><small>Your balance, credits and transactions</small></span>
                      <span aria-hidden="true">→</span>
                    </Link>
                  ) : null}
                  {organisation.canLedger ? (
                    <button className="ledger-tool ledger-tool-advanced" type="button" onClick={() => openLedger(id)} disabled={Boolean(openingOrganisationId)}>
                      <span aria-hidden="true">↗</span>
                      <span><strong>Advanced Ledger workspace</strong><small>Open the connected server workspace</small></span>
                      <span aria-hidden="true">{opening ? '…' : '↗'}</span>
                    </button>
                  ) : null}
                </div>
                {picker && picker.orgId === id ? (
                  <InstallationPicker
                    picker={picker}
                    organisationName={name}
                    busy={Boolean(openingOrganisationId)}
                    onOpen={(installationUuid) => openLedger(id, installationUuid)}
                    onRefresh={() => loadPicker(id)}
                  />
                ) : null}
              </article>
            );
          })}
        </section>
      </div>
    </Layout>
  );
}
